import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowLeft, Info } from "lucide-react";
import { useLang } from "../lib/i18n";
import DataControls from "../components/DataControls";

const LAST_UPDATED = "December 2025";

const LegalLayout = ({ titleKey, defaultTitle, kickerKey, defaultKicker, introKey, defaultIntro, noticeKey, defaultNotice, updated, children, testId }) => {
  const { t } = useLang();
  const title = t(titleKey, defaultTitle);
  const kicker = t(kickerKey, defaultKicker);
  const intro = introKey ? t(introKey, defaultIntro) : null;
  const notice = noticeKey ? t(noticeKey, defaultNotice) : null;
  return (
  <div className="min-h-screen bg-[#0a0a0c] text-white font-body" data-testid={testId}>
    {/* Top nav */}
    <header className="border-b border-white/5 bg-[#0a0a0c]/80 backdrop-blur sticky top-0 z-40">
      <div className="max-w-3xl mx-auto px-5 md:px-8 h-14 md:h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="legal-logo">
          <div className="w-7 h-7 rounded-md bg-[#ccff00] flex items-center justify-center">
            <MapPin className="w-3.5 h-3.5 text-[#0a0a0c]" strokeWidth={2.5} />
          </div>
          <span className="font-heading font-semibold tracking-tight text-sm md:text-base">
            SpotDrop<span className="text-[#ccff00]">.</span> Malta
          </span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs md:text-sm text-zinc-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> {t("common.back")}
        </Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-5 md:px-8 pt-12 md:pt-20 pb-24">
      <div className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
        {kicker}
      </div>
      <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tighter mt-3" data-testid="legal-title">
        {title}
      </h1>
      <p className="text-zinc-500 text-xs md:text-sm mt-3">
        Last updated: {updated || LAST_UPDATED}
      </p>
      {intro && (
        <p className="text-zinc-300 text-sm md:text-base mt-6 leading-relaxed" data-testid="legal-intro">
          {intro}
        </p>
      )}
      {notice && (
        <div className="mt-5 p-3 rounded-xl bg-white/[0.03] border border-white/10 flex items-start gap-2.5 text-[12px] text-zinc-400" data-testid="legal-notice">
          <Info className="w-3.5 h-3.5 mt-0.5 text-[#ccff00] shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      <div className="mt-10 md:mt-12 space-y-7 text-zinc-300 text-[15px] md:text-base leading-relaxed legal-content">
        {children}
      </div>

      <div className="mt-16 pt-8 border-t border-white/5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-zinc-500">
        <Link to="/terms" className="hover:text-white transition-colors">{t("footer.terms")}</Link>
        <Link to="/privacy" className="hover:text-white transition-colors">{t("footer.privacy")}</Link>
        <Link to="/disclaimer" className="hover:text-white transition-colors">{t("footer.disclaimer")}</Link>
        <Link to="/" className="hover:text-white transition-colors ml-auto">spotdrop.mt</Link>
      </div>
    </main>

    <style>{`
      .legal-content h2 {
        font-family: 'Outfit', sans-serif;
        font-size: 1.5rem;
        font-weight: 600;
        color: #fafafa;
        letter-spacing: -0.01em;
        margin-top: 1.5rem;
      }
      .legal-content p { color: #d4d4d8; }
      .legal-content ul { list-style: disc; padding-left: 1.25rem; color: #d4d4d8; }
      .legal-content li { margin-top: 0.4rem; }
      .legal-content strong { color: #fafafa; }
      .legal-content a { color: #ccff00; }
    `}</style>
  </div>
  );
};

/* ========= TERMS & CONDITIONS ========= */
export const Terms = () => (
  <LegalLayout
    titleKey="legal.terms.title"
    defaultTitle="Terms & Conditions"
    kickerKey="footer.terms"
    defaultKicker="Legal"
    introKey="legal.terms.intro"
    defaultIntro="These terms govern your use of SpotDrop. Please read them before using the service."
    noticeKey="legal.terms.notice"
    defaultNotice="This document is currently maintained in English. A translated version is coming soon."
    testId="page-terms"
  >
    <p>
      Welcome to SpotDrop Malta ("SpotDrop", "we", "us"). By accessing or using our
      website, mobile app, or services (collectively, the "Platform"), you agree to be
      bound by these Terms & Conditions.
    </p>

    <h2>1. What SpotDrop is</h2>
    <p>
      SpotDrop is an <strong>information and community platform</strong> that lets users
      voluntarily share information about parking availability in Malta and list or
      discover private parking spaces for rent. SpotDrop does <strong>not</strong> own,
      control, reserve, sell, or guarantee public parking spaces.
    </p>

    <h2>2. Public parking</h2>
    <ul>
      <li>Parking availability shared via SpotDrop is community-sourced and provided "as is".</li>
      <li>Public parking remains subject to local laws, road rules, signage, permits and enforcement.</li>
      <li>SpotDrop does not reserve a spot for you — a spot shared by another user may already be taken when you arrive.</li>
      <li>You are responsible for safe and legal driving and parking, including all fines, penalties, towing costs, accidents and disputes.</li>
    </ul>

    <h2>3. Private parking rentals</h2>
    <ul>
      <li>Listings for monthly or private parking are agreements directly between the space owner and the renter.</li>
      <li>SpotDrop acts only as a platform/intermediary and does not guarantee the availability, condition, legality, ownership, suitability or pricing of any private parking space.</li>
      <li>Users must verify the legitimacy of any listing before sending payment or signing any agreement.</li>
    </ul>

    <h2>4. Acceptable use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Post false, misleading, fraudulent, or unlawful information.</li>
      <li>Use the Platform to harass, defraud, or harm any other user.</li>
      <li>Attempt to disrupt, reverse engineer, or abuse the Platform.</li>
    </ul>

    <h2>5. Limitation of liability</h2>
    <p>
      SpotDrop is provided on an "as is" and "as available" basis. To the maximum extent
      permitted by law, SpotDrop and its operators are not liable for any direct,
      indirect, incidental, or consequential damages arising from the use of the
      Platform, including loss of a parking spot, fines, vehicle damage, theft, accidents,
      or disputes between users.
    </p>

    <h2>6. Changes</h2>
    <p>
      We may update these Terms from time to time. Continued use of the Platform after
      changes means you accept the updated Terms.
    </p>

    <h2>7. Contact</h2>
    <p>
      Questions about these Terms? Reach us at:
    </p>
    <ul>
      <li>Email: <a href="mailto:spotdropmalta@gmail.com">spotdropmalta@gmail.com</a></li>
      <li>Instagram: <a href="https://instagram.com/spotdropmalta" target="_blank" rel="noopener noreferrer">@spotdropmalta</a></li>
    </ul>
  </LegalLayout>
);

/* ========= PRIVACY POLICY ========= */
export const Privacy = () => (
  <LegalLayout
    titleKey="legal.privacy.title"
    defaultTitle="Privacy Policy"
    kickerKey="footer.privacy"
    defaultKicker="Privacy"
    introKey="legal.privacy.intro"
    defaultIntro="We take your privacy seriously. Here's how SpotDrop handles your data."
    noticeKey="legal.privacy.notice"
    defaultNotice="This document is currently maintained in English. A translated version is coming soon."
    testId="page-privacy"
    updated="June 2026"
  >
    <p>
      Your privacy matters. This policy explains what we collect, why, and what choices
      you have.
    </p>

    <h2>1. No accounts</h2>
    <p>
      SpotDrop has no sign-up, no password and no phone number. The app stores a random
      <strong> device ID</strong> in your browser so it can remember your spots, your
      giveaway entries and your groups. It is not linked to your name and is not an
      advertising identifier.
    </p>

    <h2>2. What we collect</h2>
    <ul>
      <li><strong>Precise location</strong> — only while the app is open, to show nearby parking spots, to place a spot you drop and to centre the map. We never track your location in the background.</li>
      <li><strong>Nickname and plate hint</strong> — the nickname and last 3 plate characters you enter, shown to the other driver during a handover so you can recognise each other.</li>
      <li><strong>Parking activity</strong> — the spots you drop, claim, hand over or report.</li>
      <li><strong>Saved car position</strong> — “Save my car” is stored on your device only. It leaves your device solely if you choose to share it with a private group.</li>
      <li><strong>Giveaway details</strong> — the email address or Instagram handle you enter to register for a giveaway, so we can contact a winner.</li>
      <li><strong>Referral and campaign data</strong> — your invite code, invite link visits and the entries you earned.</li>
      <li><strong>Friends &amp; Family groups</strong> — the private groups you create or join, your nickname inside them and when you joined.</li>
      <li><strong>Updates list</strong> — if you sign up on the website: your name, email and area in Malta.</li>
    </ul>
    <p>We do not collect payment information.</p>

    <h2>3. Optional sharing — off by default</h2>
    <ul>
      <li><strong>Live location in a group</strong> — off until you switch it on. Only members of that private group can see it, only while SpotDrop is open, and it stops automatically after an hour of inactivity. “Stop sharing my location” ends it immediately and your coordinates are deleted from our database at that moment.</li>
      <li><strong>Parked car in a group</strong> — off by default. Your saved car position is shared only while you enable “Share my parked car”, and the coordinates are deleted the moment you switch it off.</li>
    </ul>
    <p>
      Nobody outside a group you joined can see your live location or your parked car.
    </p>

    <h2>4. How we use your data</h2>
    <ul>
      <li>To run the service: live spots, handovers, private groups and giveaway entries.</li>
      <li>To keep it safe: detecting fake spots, repeat abuse and banned devices.</li>
      <li>To understand which Malta areas to prioritise for coverage.</li>
    </ul>
    <p>
      We do not sell or rent your data, and we do not use it for advertising or profiling.
    </p>

    <h2>5. Who else sees it</h2>
    <ul>
      <li><strong>Other drivers</strong> — a spot you drop is visible to nearby drivers together with your nickname and plate hint.</li>
      <li><strong>Your group members</strong> — only what you chose to share, and only while you share it.</li>
      <li><strong>Service providers</strong> — map tiles (OpenStreetMap / CARTO) plus our hosting and database providers process requests to deliver the service.</li>
    </ul>

    <h2>6. Retention</h2>
    <ul>
      <li>Parking spots expire automatically shortly after their duration ends.</li>
      <li>Shared coordinates are removed as soon as you stop sharing.</li>
      <li>Giveaway data is kept until the giveaway ends and any prize is delivered.</li>
      <li>Reports and ban records are kept as long as needed to prevent abuse.</li>
    </ul>

    <h2>7. Your data controls</h2>
    <p>
      You can see exactly what this device has stored and delete it. Deleting also removes
      your giveaway entries and your invite code, and it cannot be undone. Records that
      belong to another driver or that exist to prevent abuse are listed for you instead of
      being silently removed.
    </p>
    <DataControls />
    <p>
      You can also contact us for any data request:
    </p>
    <ul>
      <li>Email: <a href="mailto:spotdropmalta@gmail.com">spotdropmalta@gmail.com</a></li>
      <li>Instagram: <a href="https://instagram.com/spotdropmalta" target="_blank" rel="noopener noreferrer">@spotdropmalta</a></li>
    </ul>

    <h2>8. Cookies</h2>
    <p>
      The website does not use marketing or tracking cookies. SpotDrop uses local storage
      for your device ID, driver profile and saved car, and the admin page uses local
      storage to remember the admin key for the session.
    </p>

    <h2>9. Updates</h2>
    <p>
      We may update this policy as the product evolves. Material changes will be announced
      via email to updates-list members.
    </p>
  </LegalLayout>
);

/* ========= DISCLAIMER ========= */
export const Disclaimer = () => (
  <LegalLayout
    titleKey="legal.disclaimer.title"
    defaultTitle="Disclaimer"
    kickerKey="footer.disclaimer"
    defaultKicker="Disclaimer"
    introKey="legal.disclaimer.intro"
    defaultIntro="SpotDrop is an information and community platform. Public parking is never reserved or guaranteed."
    noticeKey="legal.disclaimer.notice"
    defaultNotice="This document is currently maintained in English. A translated version is coming soon."
    testId="page-disclaimer"
  >
    <p>
      Please read this disclaimer carefully before using SpotDrop Malta.
    </p>

    <h2>Information platform only</h2>
    <p>
      SpotDrop is an <strong>information and community platform</strong>. We do not own,
      control, reserve, sell, or guarantee public parking spaces. All parking availability
      shared on the Platform is community-sourced and provided voluntarily by users.
    </p>

    <h2>No reservation, no guarantee</h2>
    <p>
      A spot shared by another user may already be taken by the time you arrive. SpotDrop
      cannot reserve a public parking space on your behalf and makes no guarantee about
      availability, suitability, legality, or condition of any parking spot — public or
      private.
    </p>

    <h2>Public parking remains regulated</h2>
    <p>
      All public parking is subject to local laws, road rules, signage, permits, and
      enforcement by Maltese authorities. Using SpotDrop does not exempt you from any of
      these rules. You remain fully responsible for:
    </p>
    <ul>
      <li>Safe and legal driving and parking</li>
      <li>Any fines, penalties, or towing costs</li>
      <li>Accidents involving your vehicle</li>
      <li>Disputes with other drivers or property owners</li>
    </ul>

    <h2>Private parking rentals</h2>
    <p>
      Private parking listings are agreements between the space owner and the renter.
      SpotDrop is <strong>not</strong> a party to those agreements and does not verify,
      endorse, or guarantee the legitimacy, ownership, condition, or availability of any
      listing. Always verify a listing in person before sending payment.
    </p>

    <h2>No professional advice</h2>
    <p>
      Nothing on the Platform constitutes legal, financial, or insurance advice. If you
      need such advice, please consult a qualified professional.
    </p>

    <h2>Use at your own risk</h2>
    <p>
      Your use of SpotDrop is entirely at your own risk. By using the Platform, you
      acknowledge and accept the limitations described in our Terms & Conditions.
    </p>

    <h2>Disabled parking bays & Blue Badge</h2>
    <p>
      SpotDrop only helps users share parking information. Users must always follow
      local parking laws, road signs, time restrictions, resident permits and Blue
      Badge rules. <strong>Disabled parking bays are only for authorised Blue Badge
      holders.</strong> Using a disabled parking bay without a valid Blue Badge is
      illegal and SpotDrop accepts no responsibility for any fines, penalties or
      enforcement that result from misuse.
    </p>
  </LegalLayout>
);
